import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { addPagesToFooterMenu, generateCompliance, listShopifyConnections, pushComplianceToShopify } from "../lib/api";
import { ComplianceHealthPanel } from "./ComplianceHealthPanel";
import { t } from "../i18n";
import {
  Sparkle,
  Buildings,
  Copy,
  Check,
  Warning,
  Download,
  FileText,
  CircleNotch,
  CloudArrowUp,
  Storefront,
  ListPlus,
} from "@phosphor-icons/react";

function FooterMenuResult({ result }) {
  if (!result) return null;
  if (result.error === "scope") {
    return (
      <div className="mt-3 border border-amber-400 bg-amber-50 p-3 text-[11px] font-mono text-amber-900" data-testid="footer-menu-scope-error">
        <strong>Permission Shopify manquante.</strong> Votre Custom App doit avoir les accès <code>read_online_store_navigation</code> et <code>write_online_store_navigation</code> :
        <ol className="list-decimal ml-5 mt-1 space-y-0.5">
          <li>Shopify admin → Paramètres → Applications → Développer des applications → votre app.</li>
          <li>Configuration → Accès à l&apos;API Admin → cochez ces 2 permissions → Enregistrer.</li>
          <li>Onglet « Identifiants API » → Réinstaller l&apos;app (le jeton reste le même dans la plupart des cas ; sinon, reconnectez la boutique dans l&apos;onglet Shopify).</li>
        </ol>
      </div>
    );
  }
  if (result.error) {
    return <div className="mt-3 text-[11px] font-mono text-rose-700" data-testid="footer-menu-error">✕ {result.error}</div>;
  }
  return (
    <div className="mt-3 border-t border-emerald-200 pt-3 text-[11px] font-mono space-y-1" data-testid="footer-menu-result">
      <div className="text-emerald-800">
        Menu « {result.menu_title} »{result.menu_created ? " (créé)" : ""} — ✓ Ajoutées : {result.added.length} · ↺ Déjà présentes : {result.already_present.length}
      </div>
      {result.added.length > 0 && <div className="text-neutral-700">+ {result.added.join(", ")}</div>}
      {result.missing_pages.length > 0 && (
        <div className="text-amber-800" data-testid="footer-menu-missing">
          Pages introuvables dans la boutique (cliquez d&apos;abord sur « Pousser dans Shopify ») : {result.missing_pages.join(", ")}
        </div>
      )}
    </div>
  );
}

const TEMPLATE_TABS_BY_LANG = {
  fr: [
    { key: "footer_html", label: "Footer HTML", icon: "🌐", ext: "html" },
    { key: "legal_notice", label: "Mentions Légales", icon: "⚖️", ext: "md" },
    { key: "terms_of_sale", label: "CGV", icon: "📋", ext: "md" },
    { key: "privacy_policy", label: "Confidentialité", icon: "🔒", ext: "md" },
    { key: "shipping_policy", label: "Livraison", icon: "📦", ext: "md" },
    { key: "return_policy", label: "Retours", icon: "↩️", ext: "md" },
    { key: "warranty", label: "Garantie", icon: "🛡️", ext: "md" },
    { key: "our_story", label: "Notre Histoire", icon: "📖", ext: "md" },
    { key: "about_us", label: "Qui Sommes-Nous", icon: "💼", ext: "md" },
    { key: "refurbished_policy", label: "Reconditionné", icon: "♻️", ext: "md" },
    { key: "faq", label: "FAQ", icon: "❓", ext: "md" },
    { key: "withdrawal_form", label: "Rétractation", icon: "📝", ext: "md" },
    { key: "terms_of_use", label: "CGU", icon: "📜", ext: "md" },
    { key: "payment_methods", label: "Paiement", icon: "💳", ext: "md" },
  ],
  en: [
    { key: "footer_html", label: "Footer HTML", icon: "🌐", ext: "html" },
    { key: "legal_notice", label: "Legal Notice", icon: "⚖️", ext: "md" },
    { key: "terms_of_sale", label: "Terms of Service", icon: "📋", ext: "md" },
    { key: "privacy_policy", label: "Privacy", icon: "🔒", ext: "md" },
    { key: "shipping_policy", label: "Shipping", icon: "📦", ext: "md" },
    { key: "return_policy", label: "Refund", icon: "↩️", ext: "md" },
    { key: "warranty", label: "Warranty", icon: "🛡️", ext: "md" },
    { key: "our_story", label: "Our Story", icon: "📖", ext: "md" },
    { key: "about_us", label: "About Us", icon: "💼", ext: "md" },
    { key: "refurbished_policy", label: "Refurbished", icon: "♻️", ext: "md" },
    { key: "faq", label: "FAQ", icon: "❓", ext: "md" },
    { key: "withdrawal_form", label: "Withdrawal Form", icon: "📝", ext: "md" },
    { key: "terms_of_use", label: "Terms of Use", icon: "📜", ext: "md" },
    { key: "payment_methods", label: "Payment", icon: "💳", ext: "md" },
  ],
  es: [
    { key: "footer_html", label: "Footer HTML", icon: "🌐", ext: "html" },
    { key: "legal_notice", label: "Aviso Legal", icon: "⚖️", ext: "md" },
    { key: "terms_of_sale", label: "Términos", icon: "📋", ext: "md" },
    { key: "privacy_policy", label: "Privacidad", icon: "🔒", ext: "md" },
    { key: "shipping_policy", label: "Envío", icon: "📦", ext: "md" },
    { key: "return_policy", label: "Devolución", icon: "↩️", ext: "md" },
    { key: "warranty", label: "Garantía", icon: "🛡️", ext: "md" },
    { key: "our_story", label: "Nuestra Historia", icon: "📖", ext: "md" },
    { key: "about_us", label: "Quiénes Somos", icon: "💼", ext: "md" },
    { key: "refurbished_policy", label: "Reacondicionado", icon: "♻️", ext: "md" },
    { key: "faq", label: "FAQ", icon: "❓", ext: "md" },
    { key: "withdrawal_form", label: "Desistimiento", icon: "📝", ext: "md" },
    { key: "terms_of_use", label: "Condiciones de Uso", icon: "📜", ext: "md" },
    { key: "payment_methods", label: "Pago", icon: "💳", ext: "md" },
  ],
  it: [
    { key: "footer_html", label: "Footer HTML", icon: "🌐", ext: "html" },
    { key: "legal_notice", label: "Note Legali", icon: "⚖️", ext: "md" },
    { key: "terms_of_sale", label: "Termini", icon: "📋", ext: "md" },
    { key: "privacy_policy", label: "Privacy", icon: "🔒", ext: "md" },
    { key: "shipping_policy", label: "Spedizione", icon: "📦", ext: "md" },
    { key: "return_policy", label: "Reso", icon: "↩️", ext: "md" },
    { key: "warranty", label: "Garanzia", icon: "🛡️", ext: "md" },
    { key: "our_story", label: "La Nostra Storia", icon: "📖", ext: "md" },
    { key: "about_us", label: "Chi Siamo", icon: "💼", ext: "md" },
    { key: "refurbished_policy", label: "Ricondizionato", icon: "♻️", ext: "md" },
    { key: "faq", label: "FAQ", icon: "❓", ext: "md" },
    { key: "withdrawal_form", label: "Recesso", icon: "📝", ext: "md" },
    { key: "terms_of_use", label: "Condizioni d'Uso", icon: "📜", ext: "md" },
    { key: "payment_methods", label: "Pagamento", icon: "💳", ext: "md" },
  ],
  pt: [
    { key: "footer_html", label: "Footer HTML", icon: "🌐", ext: "html" },
    { key: "legal_notice", label: "Avisos Legais", icon: "⚖️", ext: "md" },
    { key: "terms_of_sale", label: "Termos", icon: "📋", ext: "md" },
    { key: "privacy_policy", label: "Privacidade", icon: "🔒", ext: "md" },
    { key: "shipping_policy", label: "Envio", icon: "📦", ext: "md" },
    { key: "return_policy", label: "Devolução", icon: "↩️", ext: "md" },
    { key: "warranty", label: "Garantia", icon: "🛡️", ext: "md" },
    { key: "our_story", label: "Nossa História", icon: "📖", ext: "md" },
    { key: "about_us", label: "Quem Somos", icon: "💼", ext: "md" },
    { key: "refurbished_policy", label: "Recondicionado", icon: "♻️", ext: "md" },
    { key: "faq", label: "FAQ", icon: "❓", ext: "md" },
    { key: "withdrawal_form", label: "Livre Resolução", icon: "📝", ext: "md" },
    { key: "terms_of_use", label: "Condições de Uso", icon: "📜", ext: "md" },
    { key: "payment_methods", label: "Pagamento", icon: "💳", ext: "md" },
  ],
  de: [
    { key: "footer_html", label: "Footer HTML", icon: "🌐", ext: "html" },
    { key: "legal_notice", label: "Impressum", icon: "⚖️", ext: "md" },
    { key: "terms_of_sale", label: "AGB", icon: "📋", ext: "md" },
    { key: "privacy_policy", label: "Datenschutz", icon: "🔒", ext: "md" },
    { key: "shipping_policy", label: "Versand", icon: "📦", ext: "md" },
    { key: "return_policy", label: "Widerruf", icon: "↩️", ext: "md" },
    { key: "warranty", label: "Garantie", icon: "🛡️", ext: "md" },
    { key: "our_story", label: "Unsere Geschichte", icon: "📖", ext: "md" },
    { key: "about_us", label: "Über Uns", icon: "💼", ext: "md" },
    { key: "refurbished_policy", label: "Generalüberholt", icon: "♻️", ext: "md" },
    { key: "faq", label: "FAQ", icon: "❓", ext: "md" },
    { key: "withdrawal_form", label: "Widerrufsformular", icon: "📝", ext: "md" },
    { key: "terms_of_use", label: "Nutzungsbedingungen", icon: "📜", ext: "md" },
    { key: "payment_methods", label: "Zahlung", icon: "💳", ext: "md" },
  ],
};

const PAYMENT_OPTIONS = [
  { code: "card", label: "CB Visa / Mastercard" },
  { code: "amex", label: "American Express" },
  { code: "paypal", label: "PayPal" },
  { code: "apple_pay", label: "Apple Pay" },
  { code: "google_pay", label: "Google Pay" },
  { code: "shop_pay", label: "Shop Pay" },
  { code: "bank_transfer", label: "Virement bancaire" },
  { code: "klarna", label: "Klarna (plusieurs fois)" },
];

function PaymentMethodsPicker({ value, onChange }) {
  const toggle = (code) =>
    onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code]);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="compliance-payment-methods">
      {PAYMENT_OPTIONS.map((opt) => {
        const on = value.includes(opt.code);
        return (
          <label
            key={opt.code}
            className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-[11px] font-mono cursor-pointer ma-trans ${
              on ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-900"
            }`}
          >
            <input
              type="checkbox"
              className="accent-violet-700"
              checked={on}
              onChange={() => toggle(opt.code)}
              data-testid={`compliance-payment-${opt.code}`}
            />
            {opt.label}
          </label>
        );
      })}
    </div>
  );
}

const OUTPUT_LANGUAGES = [
  { code: "fr", label: "Français (FR)", flag: "🇫🇷" },
  { code: "en", label: "English (EN)", flag: "🇬🇧" },
  { code: "es", label: "Español (ES)", flag: "🇪🇸" },
  { code: "it", label: "Italiano (IT)", flag: "🇮🇹" },
  { code: "pt", label: "Português (PT)", flag: "🇵🇹" },
  { code: "de", label: "Deutsch (DE)", flag: "🇩🇪" },
];

function CopyBtn({ text, label = "Copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          toast.success("Copié dans le presse-papier");
          setTimeout(() => setDone(false), 1500);
        } catch {
          toast.error("Impossible de copier");
        }
      }}
      className="inline-flex items-center gap-1.5 border border-neutral-900 bg-white text-neutral-900 hover:bg-neutral-100 px-3 py-1.5 text-[11px] uppercase tracking-widest font-mono ma-trans"
      data-testid="compliance-copy-btn"
    >
      {done ? <Check size={12} weight="bold" /> : <Copy size={12} />}
      {done ? "Copié" : label}
    </button>
  );
}

function DownloadBtn({ text, filename }) {
  return (
    <button
      type="button"
      onClick={() => {
        const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }}
      className="inline-flex items-center gap-1.5 border border-neutral-900 bg-white text-neutral-900 hover:bg-neutral-100 px-3 py-1.5 text-[11px] uppercase tracking-widest font-mono ma-trans"
      data-testid="compliance-download-btn"
    >
      <Download size={12} />
      .{filename.split(".").pop()}
    </button>
  );
}

export default function ComplianceWizard({ lang }) {
  const [form, setForm] = useState({
    company_name: "",
    legal_form: "",
    siret_or_reg: "",
    vat_number: "",
    address: "",
    phone: "",
    email: "",
    hours: "Lun-Ven 9h-18h",
    domain: "",
    director_name: "",
    language: lang || "fr",
    return_window_days: 14,
    shipping_processing_days: "1-2",
    shipping_transit_days: "3-5",
    shipping_price_eur: "4.90",
    free_shipping_threshold_eur: "50",
    shipping_countries: "France, Belgique, Suisse, Espagne, Italie",
    restocking_fee: false,
    refund_delay_business_days: "5-7",
    warranty_years: 2,
    founding_year: "",
    mission: "",
    registry_data: "",
    payment_methods: ["card", "paypal", "apple_pay", "google_pay"],
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState("footer_html");
  const [conns, setConns] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuResult, setMenuResult] = useState(null);

  const addToFooter = async () => {
    if (!conns.length) return;
    setMenuLoading(true);
    setMenuResult(null);
    try {
      const res = await addPagesToFooterMenu({ connection_id: conns[0].id, language: form.language });
      setMenuResult(res);
      toast.success(res.added.length ? `${res.added.length} page(s) ajoutée(s) au menu « ${res.menu_title} »` : "Toutes les pages sont déjà dans le menu footer");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setMenuResult({ error: detail === "missing_navigation_scope" ? "scope" : detail || err.message });
      toast.error(detail === "missing_navigation_scope" ? "Permission Shopify manquante (navigation)" : "Échec de l'ajout au menu footer");
    } finally {
      setMenuLoading(false);
    }
  };

  useEffect(() => {
    listShopifyConnections().then(setConns).catch(() => setConns([]));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.company_name.trim() || !form.address.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Remplissez au minimum le nom, l'adresse, l'email et le téléphone");
      return;
    }
    if ((form.payment_methods || []).length === 0) {
      toast.error("Cochez au moins un moyen de paiement");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        ...form,
        shipping_countries: form.shipping_countries
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const res = await generateCompliance(payload);
      setResult(res);
      setActiveTab("legal_notice");
      toast.success("Templates générés — copiez-les ou téléchargez-les");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || err.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const pushToShopify = async () => {
    if (!conns || conns.length === 0) {
      toast.error("Aucune connexion Shopify active — connectez d'abord votre boutique dans l'onglet Shopify");
      return;
    }
    const connection_id = conns[0].id;
    if ((form.payment_methods || []).length === 0) {
      toast.error("Cochez au moins un moyen de paiement");
      return;
    }
    setPushing(true);
    setPushResult(null);
    try {
      const payload = {
        connection_id,
        info: {
          ...form,
          shipping_countries: form.shipping_countries
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        },
      };
      const res = await pushComplianceToShopify(payload);
      setPushResult(res);
      if (res.total_errors === 0) {
        toast.success(`${res.total_created} créées, ${res.total_updated} mises à jour sur Shopify`);
      } else if (res.total_created + res.total_updated > 0) {
        toast.warning(`Partiel : ${res.total_created + res.total_updated} OK, ${res.total_errors} erreurs`);
      } else {
        toast.error(`Échec : ${res.total_errors} erreurs`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Erreur");
    } finally {
      setPushing(false);
    }
  };

  const inputCls =
    "w-full h-10 border border-neutral-900 px-3 text-sm font-mono focus:outline-none focus:bg-neutral-50";
  const labelCls = "block text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-700 mb-1";

  const activeTabs = TEMPLATE_TABS_BY_LANG[form.language] || TEMPLATE_TABS_BY_LANG.fr;
  const activeContent = result ? result[activeTab] || "" : "";

  return (
    <div className="space-y-6" data-testid="compliance-wizard">
      <ComplianceHealthPanel lang={lang} wizardLang={form.language} />
      <div className="border-l-4 border-violet-700 bg-violet-50 p-4">
        <div className="flex items-start gap-3">
          <Sparkle size={20} weight="fill" className="text-violet-700 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-sm uppercase tracking-[0.18em] font-mono font-bold text-violet-900">
              Compliance Wizard — Footer + 13 pages conformes UE
            </h2>
            <p className="text-xs font-mono text-neutral-700 mt-1 leading-relaxed">
              Génère votre footer conforme RGPD/Google Merchant + les 5 pages légales obligatoires
              (Mentions Légales, CGV, Confidentialité, Livraison, Retours) + Garantie, Notre Histoire, Qui Sommes-Nous, Reconditionné,
              FAQ, Formulaire de rétractation, CGU et Méthodes de paiement en remplissant ce formulaire.
              Les templates sont basés sur la directive européenne 2011/83/UE et les politiques GMC.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3" data-testid="compliance-form">
        <div className="md:col-span-2 bg-violet-50 border border-violet-300 p-3">
          <label className="block text-[10px] uppercase tracking-[0.2em] font-mono text-violet-900 mb-2 font-bold">
            🌍 Langue des templates générés
          </label>
          <div className="flex flex-wrap gap-1.5" data-testid="compliance-language-selector">
            {OUTPUT_LANGUAGES.map((opt) => (
              <button
                type="button"
                key={opt.code}
                onClick={() => update("language", opt.code)}
                className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest ma-trans ${
                  form.language === opt.code
                    ? "bg-violet-700 text-white border-violet-700"
                    : "bg-white text-neutral-700 border-neutral-300 hover:border-violet-700"
                }`}
                data-testid={`compliance-lang-${opt.code}`}
              >
                <span>{opt.flag}</span>
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
          <div className="mt-1 text-[10px] font-mono text-violet-700">
            Les 13 pages et le footer seront générés dans cette langue.
          </div>
        </div>

        <div className="md:col-span-2 text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 border-b border-neutral-200 pb-1">
          Identité entreprise
        </div>
        <div>
          <label className={labelCls}>Nom officiel *</label>
          <input className={inputCls} value={form.company_name} onChange={(e) => update("company_name", e.target.value)} placeholder="Joyas de Bohemia SL" data-testid="compliance-input-company" />
        </div>
        <div>
          <label className={labelCls}>Forme juridique</label>
          <input className={inputCls} value={form.legal_form} onChange={(e) => update("legal_form", e.target.value)} placeholder="SL, SAS, SARL, Auto-entrepreneur..." data-testid="compliance-input-legal-form" />
        </div>
        <div>
          <label className={labelCls}>SIRET / RCS / N° immatriculation</label>
          <input className={inputCls} value={form.siret_or_reg} onChange={(e) => update("siret_or_reg", e.target.value)} placeholder="RCS Paris B 123 456 789" data-testid="compliance-input-siret" />
        </div>
        <div>
          <label className={labelCls}>TVA Intracommunautaire</label>
          <input className={inputCls} value={form.vat_number} onChange={(e) => update("vat_number", e.target.value)} placeholder="FR12345678901" data-testid="compliance-input-vat" />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Données registrales détaillées (optionnel — recommandé pour ES, fortement conseillé pour DE/IT)</label>
          <input className={inputCls} value={form.registry_data} onChange={(e) => update("registry_data", e.target.value)} placeholder="ES: Registro Mercantil de Murcia, Sección 8, Hoja MU-118708, Inscripción 1ª, con fecha 3 de febrero de 2026 — DE: HRB 12345, Amtsgericht Berlin — FR: Capital social 10 000 €" data-testid="compliance-input-registry-data" />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Adresse complète du siège social *</label>
          <input className={inputCls} value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="123 Rue du Commerce, 75001 Paris, France" data-testid="compliance-input-address" />
        </div>

        <div className="md:col-span-2 text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 border-b border-neutral-200 pb-1 pt-2">
          Service client
        </div>
        <div>
          <label className={labelCls}>Téléphone *</label>
          <input className={inputCls} value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+33 1 00 00 00 00" data-testid="compliance-input-phone" />
        </div>
        <div>
          <label className={labelCls}>E-mail pro * (pas gmail/yahoo)</label>
          <input className={inputCls} value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="contact@votresite.com" data-testid="compliance-input-email" />
        </div>
        <div>
          <label className={labelCls}>Horaires service client</label>
          <input className={inputCls} value={form.hours} onChange={(e) => update("hours", e.target.value)} placeholder="Lun-Ven 9h-18h" data-testid="compliance-input-hours" />
        </div>
        <div>
          <label className={labelCls}>Nom de domaine</label>
          <input className={inputCls} value={form.domain} onChange={(e) => update("domain", e.target.value)} placeholder="monsite.com" data-testid="compliance-input-domain" />
        </div>

        <div className="md:col-span-2 text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 border-b border-neutral-200 pb-1 pt-2">
          Livraison
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Pays de livraison (séparés par virgules)</label>
          <input className={inputCls} value={form.shipping_countries} onChange={(e) => update("shipping_countries", e.target.value)} placeholder="France, Belgique, Suisse" data-testid="compliance-input-countries" />
        </div>
        <div>
          <label className={labelCls}>Préparation (jours ouvrés)</label>
          <input className={inputCls} value={form.shipping_processing_days} onChange={(e) => update("shipping_processing_days", e.target.value)} placeholder="1-2" data-testid="compliance-input-processing" />
        </div>
        <div>
          <label className={labelCls}>Transport (jours ouvrés)</label>
          <input className={inputCls} value={form.shipping_transit_days} onChange={(e) => update("shipping_transit_days", e.target.value)} placeholder="3-5" data-testid="compliance-input-transit" />
        </div>
        <div>
          <label className={labelCls}>Frais de livraison (€)</label>
          <input className={inputCls} value={form.shipping_price_eur} onChange={(e) => update("shipping_price_eur", e.target.value)} placeholder="4.90" data-testid="compliance-input-shipping-price" />
        </div>
        <div>
          <label className={labelCls}>Livraison gratuite dès (€)</label>
          <input className={inputCls} value={form.free_shipping_threshold_eur} onChange={(e) => update("free_shipping_threshold_eur", e.target.value)} placeholder="50" data-testid="compliance-input-free-threshold" />
        </div>

        <div className="md:col-span-2 text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 border-b border-neutral-200 pb-1 pt-2">
          Retours & remboursement
        </div>
        <div>
          <label className={labelCls}>Délai de rétractation (jours) — min. 14j UE</label>
          <input type="number" min="14" max="60" className={inputCls} value={form.return_window_days} onChange={(e) => update("return_window_days", parseInt(e.target.value, 10) || 14)} data-testid="compliance-input-return-window" />
        </div>
        <div>
          <label className={labelCls}>Délai de remboursement (jours ouvrés)</label>
          <input className={inputCls} value={form.refund_delay_business_days} onChange={(e) => update("refund_delay_business_days", e.target.value)} placeholder="5-7" data-testid="compliance-input-refund-delay" />
        </div>

        <div className="md:col-span-2 text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 border-b border-neutral-200 pb-1 pt-3">
          Méthodes de paiement acceptées
        </div>
        <div className="md:col-span-2">
          <PaymentMethodsPicker value={form.payment_methods} onChange={(v) => update("payment_methods", v)} />
          <div className="mt-1 text-[10px] font-mono text-neutral-500">
            Utilisées dans les pages « Méthodes de paiement » et « FAQ ». Cochez uniquement les moyens réellement actifs sur votre boutique.
          </div>
        </div>

        <div className="md:col-span-2 text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 border-b border-neutral-200 pb-1 pt-3">
          Garantie & Histoire (optionnel)
        </div>
        <div>
          <label className={labelCls}>Durée garantie légale (années) — défaut UE : 2</label>
          <input type="number" min="1" max="10" className={inputCls} value={form.warranty_years} onChange={(e) => update("warranty_years", parseInt(e.target.value, 10) || 2)} data-testid="compliance-input-warranty-years" />
        </div>
        <div>
          <label className={labelCls}>Année de fondation (optionnel)</label>
          <input className={inputCls} value={form.founding_year} onChange={(e) => update("founding_year", e.target.value)} placeholder="2021" data-testid="compliance-input-founding-year" />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Notre mission / Notre raison d&apos;être (optionnel — sinon texte générique)</label>
          <textarea className="w-full border border-neutral-900 px-3 py-2 text-sm font-mono focus:outline-none focus:bg-neutral-50 min-h-[70px]" value={form.mission} onChange={(e) => update("mission", e.target.value)} placeholder="Ex : proposer des produits éco-conçus à un prix juste, sans intermédiaires, avec un service client humain." data-testid="compliance-input-mission" />
        </div>

        <div className="md:col-span-2 pt-4">
          <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60 px-4 py-3 text-sm uppercase tracking-widest font-medium ma-trans" data-testid="compliance-generate-btn">
            {loading ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
            {loading ? "Génération..." : "Générer le footer + 13 pages légales"}
          </button>
        </div>
      </form>

      {result && (
        <div className="border border-neutral-900 bg-white" data-testid="compliance-result">
          {/* Push-to-Shopify hero strip */}
          <div className="border-b border-emerald-700 bg-emerald-50 px-4 py-3">
            <div className="flex flex-wrap items-center gap-3">
              <Storefront size={18} weight="fill" className="text-emerald-700 flex-shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <div className="text-[11px] uppercase tracking-[0.18em] font-mono font-bold text-emerald-900">
                  Pousser directement dans Shopify
                </div>
                <div className="text-[11px] font-mono text-neutral-700 mt-0.5">
                  {conns.length === 0
                    ? "Aucune connexion Shopify active — connectez votre boutique dans l'onglet Shopify d'abord."
                    : `Crée/met à jour les 13 pages dans ${conns[0].store_url} via l'API Shopify (zéro copier-coller).`}
                </div>
              </div>
              <button
                type="button"
                onClick={pushToShopify}
                disabled={pushing || conns.length === 0}
                className="inline-flex items-center gap-2 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 text-[11px] uppercase tracking-widest font-medium ma-trans"
                data-testid="compliance-push-shopify-btn"
              >
                {pushing ? <CircleNotch size={12} className="animate-spin" /> : <CloudArrowUp size={12} weight="bold" />}
                {pushing ? "Envoi..." : "Pousser dans Shopify"}
              </button>
              <button
                type="button"
                onClick={addToFooter}
                disabled={menuLoading || conns.length === 0}
                className="inline-flex items-center gap-2 border border-emerald-700 bg-white text-emerald-800 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 text-[11px] uppercase tracking-widest font-medium ma-trans"
                data-testid="compliance-footer-menu-btn"
              >
                {menuLoading ? <CircleNotch size={12} className="animate-spin" /> : <ListPlus size={12} weight="bold" />}
                {menuLoading ? "Ajout..." : "Ajouter au menu footer"}
              </button>
            </div>
            <FooterMenuResult result={menuResult} />
            {pushResult && (
              <div className="mt-3 border-t border-emerald-200 pt-3" data-testid="compliance-push-result">
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono">
                  <span className="text-emerald-700">✓ Créées : {pushResult.total_created}</span>
                  <span className="text-blue-700">↻ Mises à jour : {pushResult.total_updated}</span>
                  {pushResult.total_errors > 0 && (
                    <span className="text-rose-700">✕ Erreurs : {pushResult.total_errors}</span>
                  )}
                </div>
                <ul className="mt-2 space-y-0.5 text-[10px] font-mono">
                  {pushResult.results.map((r, i) => (
                    <li key={i} className={r.action === "error" ? "text-rose-700" : "text-neutral-700"}>
                      {r.action === "created" ? "✓ Créée" : r.action === "updated" ? "↻ Mise à jour" : "✕ Erreur"} —{" "}
                      <strong>{r.title}</strong>
                      {r.page_id && <span className="text-neutral-500"> (#{r.page_id})</span>}
                      {r.error && <span className="text-rose-700"> · {r.error}</span>}
                    </li>
                  ))}
                </ul>
                <div className="mt-2 text-[10px] font-mono text-neutral-600 italic">
                  {`💡 Étape finale : allez dans Shopify admin → Boutique en ligne → Navigation → Menu de pied de page et ajoutez les 13 pages au menu — ou cliquez simplement sur « Ajouter au menu footer » ci-dessus.`}
                </div>
              </div>
            )}
          </div>

          {result.warnings && result.warnings.length > 0 && (
            <div className="bg-amber-50 border-b border-amber-300 px-4 py-3">
              <div className="flex items-start gap-2">
                <Warning size={16} className="text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest font-mono font-bold text-amber-900 mb-1">
                    Avertissements ({result.warnings.length})
                  </div>
                  <ul className="text-xs font-mono text-amber-900 list-disc ml-4 space-y-1">
                    {result.warnings.map((w, i) => (<li key={i}>{w}</li>))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap border-b border-neutral-900 bg-neutral-50">
            {activeTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-2 text-[11px] uppercase tracking-widest font-mono border-r border-neutral-900 ma-trans ${
                  activeTab === tab.key ? "bg-neutral-900 text-white" : "bg-white hover:bg-neutral-100"
                }`}
                data-testid={`compliance-tab-${tab.key}`}
              >
                <span className="mr-1">{tab.icon}</span> {tab.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-2 px-3 py-2 bg-neutral-50 border-b border-neutral-200">
            <span className="text-[10px] font-mono text-neutral-600">
              {(activeContent || "").length} caractères · prêt à copier-coller dans Shopify
            </span>
            <div className="flex gap-2">
              <CopyBtn text={activeContent} label="Copier" />
              <DownloadBtn text={activeContent} filename={`${activeTab}.${activeTabs.find(t => t.key === activeTab)?.ext || 'txt'}`} />
            </div>
          </div>

          <pre className="p-4 text-xs font-mono whitespace-pre-wrap text-neutral-900 max-h-[500px] overflow-auto" data-testid="compliance-content">
            {activeContent}
          </pre>

          <div className="px-4 py-3 border-t border-neutral-200 bg-neutral-50 text-[11px] font-mono text-neutral-600">
            <strong>{`💡 Comment l'utiliser dans Shopify :`}</strong>
            <ul className="list-disc ml-5 mt-1 space-y-0.5">
              <li><strong>Footer HTML</strong> → Boutique en ligne → Thèmes → Personnaliser → bloc Personnalisé HTML dans Pied de page.</li>
              <li><strong>Mentions Légales / CGV / Confidentialité / Livraison / Retours</strong> → Paramètres → Politiques → coller dans la section correspondante → Save.</li>
              <li>Vérifiez ensuite que ces 13 pages apparaissent dans votre menu de pied de page (Navigation → Menu de pied de page).</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
